/* eslint-disable @typescript-eslint/no-explicit-any */

import { FormControl } from '@angular/forms';
import { v4 as uuidv4 } from 'uuid';
import { FilterExpressionValue, OdataFilterExpression, ODataFilterGroup } from '../../../../core';
import { TableColumnSearchType } from './table-column-search-type.enum';
import { DropdownItem } from '../../form/models/dropdown-item';
import { signal, Type, WritableSignal } from '@angular/core';
import { DateUtils } from '../../../utils/date-utils';
import { TableFilterType } from './table-filter';

export interface TableColumnConfig<T> {
  id?: string;
  title?: string;
  value?: (row: T) => string | undefined | null;
  ignoreInExport?: boolean;

  style?: {
    width?: string;
    minWidth?: string;
    cellStyle?: string;
  };

  sort?: {
    odataProperty?: string;
  };

  filter?: {
    odataProperty?: string;
    type?: TableColumnSearchType;
    dropdownItems?: WritableSignal<DropdownItem[]>;
  };

  cell?: {
    component: Type<any>;
    inputs?: (row: T, loading: boolean) => Record<string, any>;
    disableRowClickOnCell?: boolean;
  };
}

export class TableColumn<T> {
  readonly id: string;
  readonly title?: string;
  readonly value?: (row: T) => string | undefined | null;
  readonly ignoreInExport: boolean = false;

  //style
  readonly width?: string;
  readonly minWidth?: string;
  readonly cellStyle?: string;

  // sorting
  readonly sortOdataPropertyName?: string;

  // filtering
  readonly filterOdataPropertyName?: string;
  readonly filterType?: TableColumnSearchType;
  readonly filterFormControl = new FormControl();
  readonly filterDropdownItems: WritableSignal<DropdownItem[]>;

  // custom cell
  readonly cellComponent?: Type<any>;
  readonly cellComponentInputsFunction?: (row: T, loading: boolean) => Record<string, any>;
  readonly disableRowClickOnColumn: boolean = false;

  constructor(config: TableColumnConfig<T>) {
    this.id = config.id ?? uuidv4();
    this.title = config.title;
    this.ignoreInExport = config.ignoreInExport ?? false;
    this.width = config.style?.width;
    this.minWidth = config.style?.minWidth;
    this.cellStyle = config.style?.cellStyle;
    this.value = config.value;
    this.sortOdataPropertyName = config.sort?.odataProperty;
    this.filterOdataPropertyName = config.filter?.odataProperty ?? '';
    this.filterType = config.filter?.type;
    this.filterDropdownItems = config.filter?.dropdownItems ?? signal([]);
    this.cellComponent = config.cell?.component;
    this.cellComponentInputsFunction = config.cell?.inputs;
    this.disableRowClickOnColumn = config.cell?.disableRowClickOnCell ?? false;
  }

  public buildFilterExpression(): OdataFilterExpression | ODataFilterGroup | null {
    switch (this.filterType) {
      case TableColumnSearchType.options:
        return this.getOptionsFilterExpression();
      case TableColumnSearchType.number:
      case TableColumnSearchType.currency:
        return this.getNumberFilterExpression();
      case TableColumnSearchType.text:
      case TableColumnSearchType.textStartsWith:
        return this.getTextFilterExpression();
      case TableColumnSearchType.date:
        return this.getDateFilterGroup();
      case TableColumnSearchType.dateOnly:
        return this.getDateOnlyFilterExpression();
    }
    return null;
  }

  public clearFilter() {
    this.filterFormControl.reset();
  }

  private getTextFilterExpression(): OdataFilterExpression | null {
    const filterValue = this.getFilterValue();
    if (filterValue === null || filterValue === '' || !this.filterType) {
      return null;
    }

    if (this.filterType === TableColumnSearchType.textStartsWith) {
      return {
        field: this.filterOdataPropertyName ?? '',
        operator: 'startswith',
        value: filterValue,
      };
    }

    return {
      field: this.filterOdataPropertyName ?? '',
      operator: 'contains',
      value: filterValue,
    };
  }

  private getNumberFilterExpression(): OdataFilterExpression | null {
    const filterValue = this.getFilterValue();
    if (filterValue === null || filterValue === '' || !this.filterType) {
      return null;
    }
    return {
      field: this.filterOdataPropertyName ?? '',
      operator: 'eq',
      value: filterValue,
    };
  }

  private getOptionsFilterExpression(): OdataFilterExpression | null {
    const filterValue = this.getFilterValue();
    if (filterValue === null || filterValue === '' || !this.filterType) {
      return null;
    }
    return {
      field: this.filterOdataPropertyName ?? '',
      operator: 'eq',
      value: filterValue,
    };
  }

  private getDateFilterGroup(): ODataFilterGroup | null {
    const filterValueDate = this.getFilterValue() as Date;
    if (filterValueDate === null) {
      return null;
    }
    const startOfDate = DateUtils.startOfDate(filterValueDate);
    const endOfDate = DateUtils.endOfDate(filterValueDate);
    return {
      logic: 'and',
      expressions: [
        {
          field: this.filterOdataPropertyName ?? '',
          operator: 'ge',
          value: startOfDate,
        },
        {
          field: this.filterOdataPropertyName ?? '',
          operator: 'le',
          value: endOfDate,
        },
      ],
    };
  }

  private getDateOnlyFilterExpression(): OdataFilterExpression | null {
    const filterValueDate = this.getFilterValue() as Date;
    if (filterValueDate === null) {
      return null;
    }

    return {
      field: this.filterOdataPropertyName ?? '',
      operator: 'eq',
      value: filterValueDate,
      type: TableFilterType.dateOnly,
    };
  }

  private getFilterValue(): FilterExpressionValue | null {
    if (!this.filterType) {
      return null;
    }
    const filterValue = this.filterFormControl.value;
    switch (this.filterType) {
      case TableColumnSearchType.text:
      case TableColumnSearchType.textStartsWith:
      case TableColumnSearchType.options:
        return filterValue;
      case TableColumnSearchType.date:
      case TableColumnSearchType.dateOnly:
        return filterValue;
      case TableColumnSearchType.number: {
        if (filterValue === null || filterValue === '') {
          return null;
        }
        const parsedValue = parseInt(filterValue, 10);
        // If NaN, we still want the search to happen, but we don't want it to match anything
        return isNaN(parsedValue) ? Number.MAX_SAFE_INTEGER * -1 : parsedValue;
      }
      case TableColumnSearchType.currency: {
        if (filterValue === null || filterValue === '') {
          return null;
        }
        const parsedValue = parseFloat(filterValue);
        // If NaN, we still want the search to happen, but we don't want it to match anything
        return isNaN(parsedValue) ? Number.MAX_SAFE_INTEGER * -1 : parsedValue;
      }
    }
  }
}
